import {
  CognitoUserPool,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';

/**
 * Cognito configuration, read from Vite env at build time.
 *
 * These are NOT secrets — the User Pool ID and the public SPA App Client ID are
 * safe in browser code. They are injected as GitHub Actions *Variables* (see the
 * README). Reading with `?? ''` means an unset value never hard-fails the build;
 * the app instead surfaces a clear runtime error (see `assertCognitoConfigured`).
 */
export interface CognitoConfig {
  readonly userPoolId: string;
  readonly clientId: string;
  readonly region: string;
}

export const cognitoConfig: CognitoConfig = {
  userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID ?? '',
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID ?? '',
  region: import.meta.env.VITE_AWS_REGION ?? '',
};

/** True when the pool id and client id are both present. */
export const isCognitoConfigured = (): boolean =>
  Boolean(cognitoConfig.userPoolId && cognitoConfig.clientId);

export const COGNITO_MISCONFIGURED_MESSAGE =
  'Admin auth is not configured. Set VITE_COGNITO_USER_POOL_ID, ' +
  'VITE_COGNITO_CLIENT_ID and VITE_AWS_REGION (GitHub Actions Variables, or ' +
  '.env.local for local dev).';

export function assertCognitoConfigured(): void {
  if (!isCognitoConfigured()) {
    throw new Error(COGNITO_MISCONFIGURED_MESSAGE);
  }
}

let pool: CognitoUserPool | undefined;

/** Lazily-created user pool. Throws a clear error if config is missing. */
export function getUserPool(): CognitoUserPool {
  assertCognitoConfigured();
  if (!pool) {
    pool = new CognitoUserPool({
      UserPoolId: cognitoConfig.userPoolId,
      ClientId: cognitoConfig.clientId,
    });
  }
  return pool;
}

/**
 * Resolve the current valid session for the signed-in user, refreshing the
 * id/access tokens via the refresh token if they have expired. Rejects when
 * there is no signed-in user or the session cannot be refreshed (i.e. the user
 * must log in again).
 */
export function getSession(): Promise<CognitoUserSession> {
  return new Promise((resolve, reject) => {
    const user = getUserPool().getCurrentUser();
    if (!user) {
      reject(new Error('No authenticated user'));
      return;
    }
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        reject(err ?? new Error('Invalid session'));
        return;
      }
      resolve(session);
    });
  });
}

/** Sign out the current user locally (clears cached tokens). */
export function signOutCurrentUser(): void {
  const user = getUserPool().getCurrentUser();
  user?.signOut();
}
